import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faInfoCircle, faPencil, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { AddCalendarEventModal } from './AddCalendarEventModal';
import { Button } from '@skipper/ui';
import dayjs from 'dayjs';
import { getClient } from '@graphql/graphql-client/graphClient';
import { Modal } from '@components/base';
import { useDeleteEventMutation } from '@graphql/generated-graphql';
import { useQueryClient } from 'react-query';

export const EventPreviewModal = ({
    event,
    courseName,
    color,
    courseId,
    coursesList = [],
    setFirstLoad,
    setEventsReload,
}: {
    event: any;
    courseName: string;
    color: string;
    courseId: string;
    coursesList: any;
    setFirstLoad?: any;
    setEventsReload?: any;
}) => {
    const { toggle } = useUniversalModalDispatch();

    const [modalOpen, setModalOpen] = useState(false);

    const elementRef = useRef<HTMLDivElement | null>(null);
    useOnClickOutside(elementRef, () => {
        toggle(false);
    });

    const hasEditPermissions =
        event?.course?.currentEnrollment?.role?.permissions?.includes('manage_course_events');

    const startTime = dayjs(event.timeStart).format('hh:mm a');
    const endTime = dayjs(event.timeEnd).format('hh:mm a');
    const startOfDay = dayjs(event.date).startOf('day').format('hh:mm a');
    const endOfDay = dayjs(event.date).endOf('day').format('hh:mm a');

    const queryClient = useQueryClient();

    const deleteEvent = useDeleteEventMutation(getClient('calendar'), {
        onSuccess: (data) => {
            if (data?.deleteEvent?.success) {
                queryClient.invalidateQueries('CalendarEvents', {
                    refetchInactive: true,
                });
                toggle(false);
                setEventsReload({
                    updating: true,
                    oldCourse: event?.course?.id,
                    newCourse: event?.course?.id,
                });
            }
        },
    });

    const AllDay = startTime === startOfDay && endTime === endOfDay;

    return (
        <div className="fixed inset-0 overflow-y-auto flex h-full">
            <div className="relative flex-grow flex-1 flex justify-center items-center py-2">
                <div className="relative bg-white rounded-md p-5 w-[370px] md:w-[550px] font-medium text-sm">
                    <div className="flex justify-between top">
                        <div className="flex items-center w-[80%]">
                            <div
                                className="h-3 w-3 rounded-full  mr-2"
                                style={{
                                    backgroundColor: color ?? '#c61f50',
                                }}
                            />
                            <h4 className="w-[95%]">{event.title}</h4>
                        </div>

                        <div className="flex items-center">
                            {hasEditPermissions && (
                                <>
                                    <FontAwesomeIcon
                                        icon={faTrashCan}
                                        size="lg"
                                        className="cursor-pointer text-gray-500"
                                        onClick={() => {
                                            setModalOpen(true);
                                        }}
                                    />
                                    <FontAwesomeIcon
                                        icon={faPencil}
                                        size="lg"
                                        className="cursor-pointer text-gray-500 ml-3"
                                        onClick={() => {
                                            toggle(false);
                                            toggle(
                                                true,
                                                <AddCalendarEventModal
                                                    type="update"
                                                    event={event}
                                                    coursesList={coursesList}
                                                    setFirstLoad={setFirstLoad}
                                                    setEventsReload={setEventsReload}
                                                />,
                                            );
                                        }}
                                    />
                                </>
                            )}
                            <FontAwesomeIcon
                                icon={faXmark}
                                size="lg"
                                className="cursor-pointer text-gray-500 ml-3"
                                onClick={() => toggle(false)}
                            />
                        </div>
                    </div>
                    <p className="text-base leading-6 font-normal mt-2 ml-6">
                        {dayjs(event.date).format('ddd, MMM DD, YYYY')} @{' '}
                        {AllDay ? 'All Day' : `${startTime} - ${endTime}`}
                    </p>
                    <div
                        className="flex mt-10 items-center cursor-pointer"
                        onClick={() => {
                            window.open(`/courses/${courseId}/`);
                        }}>
                        <FontAwesomeIcon icon={faCalendar} size="lg" className="text-gray-500" />
                        <p className="text-teal-600 leading-6 font-normal ml-2">{courseName}</p>
                    </div>
                    {!!event.details && (
                        <div className="flex items-top cursor-pointer mt-3">
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                size="lg"
                                className="text-gray-500 mt-1"
                            />
                            <div className="text-base leading-6 font-normal ml-2">
                                <textarea
                                    readOnly
                                    value={event.details}
                                    className="w-full resize-none focus:outline-none bg-transparent"
                                    rows={10}
                                    cols={50}
                                />
                            </div>
                        </div>
                    )}
                    {!!event.url && (
                        <Button
                            variant="light"
                            size="medium"
                            className="mt-5 px-6"
                            onClick={() => {
                                const url = !event.url.match(/^https?:\/\//i)
                                    ? 'https://' + event.url
                                    : event.url;
                                window.open(url);
                            }}>
                            Visit Website
                        </Button>
                    )}
                </div>
            </div>
            <Modal
                open={modalOpen}
                setOpen={() => setModalOpen(true)}
                onCancel={() => setModalOpen(false)}
                onConfirm={() => {
                    deleteEvent.mutate({ id: event.id });
                }}
                type="danger"
                title="Delete Event?"
                body="Warning: this cannot be undone"
                confirmText="Yes, delete event"
                cancelText="Cancel"
                advancedOptions={[
                    'Event will be removed from the calendar',
                    'Members will be notified this event is cancelled  (if they have notifications toggled on)',
                ]}
            />
        </div>
    );
};
